'use client'

import {
  BazaarvoiceScriptLoader,
  useBazaarvoiceRatingObserver,
} from '@/app/components/shared/BazaarvoiceScriptLoader'
import { RatingSummary } from '@/app/components/shared/RatingSummary'
import { cx } from '@/app/lib/utils'
import { shouldShowRating } from '@/app/lib/ratings'

import styles from './BazaarvoiceReviews.module.scss'
import { BazaarvoiceWriteReview } from './BazaarvoiceWriteReview'
import type { BazaarvoiceReviewsProps } from './types'

/**
 * `BazaarvoiceReviews` — sección de reseñas de Bazaarvoice en la PDP, con el
 * mismo encabezado que el sistema propio (Figma "Valoración del evento" /
 * "Valoraciones del evento (N)" — specs/046-valoraciones-bazaarvoice-bd).
 *
 * El **listado** de opiniones sigue siendo íntegramente de Bazaarvoice: el
 * widget "Reviews Display" que pinta dentro de `data-bv-show="reviews"` trae
 * las reseñas, su paginación, sus filtros y su modal de envío, todo gestionado
 * por `bv.js` (confirmado contra el código real de eso-entradas,
 * `app/views/products/show.html.erb`).
 *
 * Lo que NO se delega es el **estado sin reseñas**. El widget trae uno propio
 * ("OPINIONES" + fila gris + "ESCRIBE LA PRIMERA OPINIÓN DE ESTE PRODUCTO",
 * alineado a la izquierda y maquetado para fondo claro) que no se parece al
 * del proyecto de diseño, que sí especifica uno para esta sección: aviso
 * centrado + cinco estrellas de 36px + "Déjanos tu opinión" (Figma 5900:37443
 * mobile / 5900:33900 desktop). Así que el contenedor nativo solo se muestra
 * cuando Bazaarvoice confirma que hay al menos una reseña que listar; por
 * debajo de eso se pinta `BazaarvoiceWriteReview`, que abre el mismo modal de
 * envío de Bazaarvoice que abriría su botón nativo.
 *
 * Ojo: el corte es "hay reseñas", no el umbral del entorno. El umbral decide
 * si se muestra la PUNTUACIÓN (FR-009/FR-010); ocultar además el listado por
 * debajo de él escondería opiniones reales ya publicadas.
 *
 * El resumen (`RatingSummary`) es la única pieza propia. El contenedor
 * `reviews` (confirmado en vivo) NO expone el agregado (conteo/media) en
 * ningún nodo de texto reconocible — solo reseñas individuales — así que el
 * dato se lee de un segundo contenedor `inline_rating`, oculto, dedicado
 * exclusivamente a esta lectura (mismo patrón que la cabecera,
 * `BazaarvoiceHeaderRating`, y las cards): reutiliza el mismo maquetado que
 * la cabecera BD (research.md R12) y solo se muestra cuando el conteo
 * alcanza el umbral del entorno (FR-009) — por debajo, no se pinta ninguna
 * puntuación numérica (FR-010), pero el widget de reseñas sigue ahí para
 * poder escribir una opinión en cuanto cargue.
 */
export function BazaarvoiceReviews({
  resource,
  ratingEnvironment,
  variant = 'default',
}: BazaarvoiceReviewsProps) {
  const {
    containerRef: ratingProbeRef,
    reviewCount,
    ratingAverage,
  } = useBazaarvoiceRatingObserver()
  const hasEnoughRatings = shouldShowRating(reviewCount, ratingEnvironment.minRatingsThreshold)
  // `reviewCount` solo deja de ser `null` cuando Bazaarvoice ha pintado un
  // agregado con al menos una reseña — mientras carga (o si nunca carga) se
  // asume "sin reseñas", que es el estado que el diseño cubre.
  const hasReviews = reviewCount != null && reviewCount > 0
  // Figma de canal: con reseñas, cabecera en fila y listado en tarjetas.
  const isChannelList = variant === 'channel' && hasReviews

  return (
    <div
      className={cx(
        styles.section,
        !hasReviews && styles.sectionEmpty,
        isChannelList && styles.sectionChannel,
      )}
      data-testid="bazaarvoice-reviews"
    >
      <BazaarvoiceScriptLoader scriptUrl={ratingEnvironment.bazaarvoiceScriptUrl} />

      {/* Probe oculto — solo para leer el agregado (conteo/media), nunca
          visible: el widget `reviews` de abajo es la experiencia real. */}
      <div
        ref={ratingProbeRef}
        data-bv-show="inline_rating"
        data-bv-product-id={resource.id}
        data-bv-seo="false"
        className={styles.hidden}
      />

      <div className={styles.headerBlock}>
        <div className={cx(styles.headingGroup, isChannelList && styles.headingGroupChannel)}>
          <h2 className={styles.heading}>
            {hasEnoughRatings
              ? `Valoraciones del evento (${reviewCount})`
              : 'Valoración del evento'}
          </h2>
          {/* `role="status"`: la aparición de la puntuación ocurre de forma
            asíncrona (`MutationObserver` sobre el marcado que Bazaarvoice
            inyecta, ver `useBazaarvoiceRatingObserver`) después del primer
            render — un usuario vidente lo nota sin más, uno de lector de
            pantalla no, salvo que se anuncie (WCAG 2.1 SC 4.1.3). */}
          <div role="status" aria-live="polite" className={styles.status}>
            {hasEnoughRatings && (
              <RatingSummary
                ratingSum={reviewCount}
                ratingAverage={ratingAverage}
                threshold={ratingEnvironment.minRatingsThreshold}
                inverse
              />
            )}
          </div>
        </div>

        <BazaarvoiceWriteReview productId={resource.id} hasReviews={hasReviews} variant={variant} />
      </div>

      {/* Contenedor real de Bazaarvoice — SIEMPRE renderizado (para que `bv.js`
          pinte dentro en cuanto cargue), pero solo visible cuando hay reseñas
          que listar: su estado vacío nativo lo sustituye el bloque de arriba,
          que sí sigue el proyecto de diseño. */}
      <div
        data-bv-show="reviews"
        data-bv-product-id={resource.id}
        className={cx(
          styles.reviewsContainer,
          !hasReviews && styles.hidden,
          isChannelList && styles.reviewsChannel,
        )}
      />
    </div>
  )
}
